// ============================================
// Main Dashboard Page
// Facebook Ads Performance Dashboard for SpaceT
// Following Layer Cake Structure from dashboard-design skill
// ============================================

import { useDateRange } from '../../stores/dateRangeStore';
import { useDashboardData } from '../../hooks/useDashboardData';
import { DateRangePicker } from '../../components/dashboard/DateRangePicker';
import { SyncStatusBar } from '../../components/dashboard/SyncStatusBar';
import { ErrorState } from '../../components/layout/LoadingState';
import { KPISection } from './components/KPISection';
import { SpendTrendChart } from './components/SpendTrendChart';
import { DailyPerformanceChart } from './components/DailyPerformanceChart';
import { TopCampaignsTable } from './components/TopCampaignsTable';
import { BusinessUnitChart } from './components/BusinessUnitChart';

export default function Dashboard() {
  const { dateRange } = useDateRange();
  const { isLoading, error, data, mutate } = useDashboardData(dateRange);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Facebook Ads Dashboard</h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Hiệu suất quảng cáo SpaceT theo Business Unit
            </p>
          </div>
          <div className="self-start sm:self-auto">
            <DateRangePicker />
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <ErrorState
          message={error.message || 'Không thể tải dữ liệu dashboard'}
          onRetry={() => {
            mutate.summary();
            mutate.spendTrend();
            mutate.businessUnits();
            mutate.dailyConversions();
            mutate.topCampaigns();
          }}
        />
      )}

      {/* ============================================
          LAYER 1: EXECUTIVE SUMMARY (Top)
          4 KPI cards với semantic colors
          Target: 5-second test - biết ngay tình hình OK hay không
          ============================================ */}
      <section>
        <KPISection
          data={data.summary?.kpis}
          isLoading={isLoading}
        />
      </section>

      {/* ============================================
          LAYER 2: DEPARTMENTAL VIEW (Middle)
          Primary Charts với trends và comparisons
          ============================================ */}

      {/* Row 1: Spend Trend + Daily Conversions */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
        {/* Spend Trend by BU - 8 cols (Primary Chart) */}
        <div className="lg:col-span-8">
          <SpendTrendChart
            data={data.spendTrend}
            isLoading={isLoading}
          />
        </div>

        {/* Daily Conversions - 4 cols */}
        <div className="lg:col-span-4">
          <DailyPerformanceChart
            data={data.dailyConversions}
            isLoading={isLoading}
          />
        </div>
      </section>

      {/* Row 2: Business Unit Comparison + Top Campaigns */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
        {/* Business Unit Comparison - 5 cols */}
        <div className="lg:col-span-5">
          <BusinessUnitChart
            data={data.businessUnits}
            isLoading={isLoading}
          />
        </div>

        {/* Top Campaigns Table - 7 cols */}
        <div className="lg:col-span-7">
          <TopCampaignsTable
            data={data.topCampaigns}
            isLoading={isLoading}
          />
        </div>
      </section>

      {/* ============================================
          LAYER 3: SYNC STATUS (Bottom)
          Data freshness indicator
          ============================================ */}
      {data.summary?.syncStatus && (
        <section>
          <SyncStatusBar sources={data.summary.syncStatus} />
        </section>
      )}
    </div>
  );
}
